// Pure rules behind a Shared Setting: which integrations can take part in
// attribute mapping, the pairs they form, and how mapping rows are derived from
// a selection. No React, no API — everything here is a plain function.

import { parseAttributeKey } from '../../../features/dynamic-form/core/attributeKey';

export const locationAttributesOf = (integration) =>
  integration.attributes.location_settings_schema || [];

// An integration with no location-level attributes can still be linked, but only
// for lifecycle sync — it has nothing to take part in attribute mapping with.
export const hasLocationAttributes = (integration) =>
  locationAttributesOf(integration).length > 0;

// Every unique ordered pair of the integrations that actually have attributes to
// map. Lifecycle-only ones never appear in a pair.
export const pairsOf = (integrations) => {
  const pairs = [];

  for (let i = 0; i < integrations.length; i += 1) {
    for (let j = i + 1; j < integrations.length; j += 1) {
      pairs.push([integrations[i], integrations[j]]);
    }
  }

  return pairs;
};

// Collapsible section per source integration. The last mappable integration is
// only ever a target, so it has no pairs of its own and gets no section.
export const groupPairsBySource = (mappableIntegrations, pairs) =>
  mappableIntegrations
    .map((integration) => ({
      integration,
      groupPairs: pairs.filter(([source]) => source.id === integration.id),
    }))
    .filter((group) => group.groupPairs.length > 0);

const stripIndex = (name) => name.replace(/\d+$/, '');

// One row per source attribute of every pair. Targets are guessed by name (exact,
// or modulo command index) unless both ends were already in the saved setting:
// the API drops unmapped rows, so an absent row there means "no mapping".
export const buildAutoMappings = (
  selectedIntegrations,
  previousMappings,
  savedIntegrationIds = new Set(),
) => {
  const mappable = selectedIntegrations.filter(hasLocationAttributes);

  return pairsOf(mappable).flatMap(([intA, intB]) => {
    const intAAttrs = locationAttributesOf(intA);
    const intBAttrs = locationAttributesOf(intB);
    const intBNames = new Set(intBAttrs.map((a) => a.name));
    const pairMappings = previousMappings.filter(
      (m) =>
        m.from_integration_uuid === intA.id &&
        m.to_integration_uuid === intB.id,
    );
    // Exact matches are reserved up front so an index-modulo guess for an
    // earlier row cannot take the target a later row matches by name.
    const claimed = new Set(
      intAAttrs.map((a) => a.name).filter((name) => intBNames.has(name)),
    );

    return intAAttrs.map((attr) => {
      // Match existing mapping by base name so dot-notation entries
      // (e.g. 'headers1.KarmakAccountNumber') are preserved correctly.
      const existing = pairMappings.find(
        (m) => parseAttributeKey(m.from_attribute_name).baseName === attr.name,
      );

      const guess = intBNames.has(attr.name)
        ? attr.name
        : intBAttrs.find(
            (a) =>
              !claimed.has(a.name) &&
              stripIndex(a.name) === stripIndex(attr.name),
          )?.name ?? '';
      if (guess) claimed.add(guess);
      const autoTarget =
        savedIntegrationIds.has(intA.id) && savedIntegrationIds.has(intB.id)
          ? ''
          : guess;

      return {
        from_integration_uuid: intA.id,
        from_attribute_name: existing
          ? existing.from_attribute_name
          : attr.name,
        to_integration_uuid: intB.id,
        to_attribute_name: existing ? existing.to_attribute_name : autoTarget,
      };
    });
  });
};

// A row only reaches the API once both ends are filled in.
export const isMappedRow = (mapping) =>
  Boolean(
    mapping.from_attribute_name?.trim() && mapping.to_attribute_name?.trim(),
  );

// The form refuses to save a selection that produced rows but mapped none of
// them — an empty Shared Setting would sync nothing.
export const noMappingsMade = (mappings) =>
  mappings.length > 0 && !mappings.some(isMappedRow);

// One pass over the mappings instead of two filters per rendered section.
export const mappingStatsBySource = (mappings) =>
  mappings.reduce((acc, m) => {
    const stats = acc[m.from_integration_uuid] ?? { mapped: 0, total: 0 };
    stats.total += 1;
    if (m.to_attribute_name?.trim()) stats.mapped += 1;
    acc[m.from_integration_uuid] = stats;
    return acc;
  }, {});

// Validation failures come back as JSON:API errors carrying a source pointer per
// attribute (e.g. '/data/attributes/integration_uuids'), which is what tells us
// which field to attach the message to.
const FIELD_ATTRIBUTES = ['name', 'integration_uuids'];

export const errorForAttribute = (errors, attribute) =>
  errors?.find?.((error) =>
    error?.source?.pointer?.endsWith(`/${attribute}`),
  ) ?? null;

// Anything that does not map onto a field still has to reach the user, so it goes
// into the notification instead of being swallowed by a generic message.
export const unplacedErrorDetail = (errors) =>
  errors?.find?.(
    (error) =>
      error?.detail &&
      !FIELD_ATTRIBUTES.some((attribute) =>
        error?.source?.pointer?.endsWith(`/${attribute}`),
      ),
  )?.detail ?? null;
